import Config from '@config';
import { initializeDepartmentsApi } from '@gasbi/shared/common/features/departments';

import axiosBaseQuery from '@/utils/axiosBaseQuery';

import {
  CreateDepartmentCommand,
  CreateDepartmentResult,
  DepartmentReadModel,
  UpdateDepartmentCommand,
} from './types';

export const {
  departmentsApi,
  useGetDepartmentsIInfiniteQuery,
  useGetActiveDepartmentsQuery,
} = initializeDepartmentsApi({
  baseUrl: Config.VITE_API_URL,
  baseQuery: axiosBaseQuery,
});

// The shared reference-book-flavoured `getDepartments`/`createDepartment`
// endpoints above don't match Gasbi.Crm.Api's actual DepartmentsController
// (custom Department + DepartmentRole entities, not a ReferenceBook).
// These endpoints target the same controller with accurate types instead.
export const crmDepartmentsApi = departmentsApi.injectEndpoints({
  endpoints: builder => ({
    getCrmDepartments: builder.query<
      DepartmentReadModel[],
      { organizationId?: string } | void
    >({
      // Only organizationId: a filter hook may hand over its whole query.
      query: args => ({
        url: '/',
        params: args ? { organizationId: args.organizationId } : undefined,
      }),
      providesTags: () => ['Department', 'Departments'],
    }),
    createCrmDepartment: builder.mutation<
      CreateDepartmentResult,
      CreateDepartmentCommand
    >({
      query: data => ({ url: '/', method: 'POST', data }),
      invalidatesTags: ['Department', 'Departments'],
    }),
    updateCrmDepartment: builder.mutation<void, UpdateDepartmentCommand>({
      query: ({ id, ...data }) => ({
        url: `/${id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['Department', 'Departments'],
    }),
    assignDepartmentRole: builder.mutation<
      void,
      { departmentId: string; roleId: string }
    >({
      query: ({ departmentId, roleId }) => ({
        url: `/${departmentId}/roles/${roleId}`,
        method: 'POST',
      }),
      invalidatesTags: ['Department', 'Departments'],
    }),
    removeDepartmentRole: builder.mutation<void, { roleId: string }>({
      query: ({ roleId }) => ({
        url: `/roles/${roleId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Department', 'Departments'],
    }),
  }),
});

export const {
  useGetCrmDepartmentsQuery,
  useCreateCrmDepartmentMutation,
  useUpdateCrmDepartmentMutation,
  useAssignDepartmentRoleMutation,
  useRemoveDepartmentRoleMutation,
} = crmDepartmentsApi;
